import type { FC } from 'react'
import { useCallback, useId, useState } from 'react'

import {
  contentSection,
  menuCoverImage,
} from '@liveHelper/components/Idle/Idle.twind'
import {
  okButton,
  requestVisitorInformationDescription,
  requestVisitorInformationFormLabel,
  requestVisitorInformationInput,
  requestVisitorInformationPrivacyInfo,
} from '@liveHelper/components/RequestVisitorInformation/RequestVisitorInformation.twind'
import { expandedHeaderImageContainer } from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  actionsSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'
import {
  FIRST_NAME_INPUT,
  LIVE_HELPER_WIDGET_BASE_CONTAINER,
  NEXT_BUTTON,
} from '@liveHelper/testIds'
import {
  useTrackLeadCaptureFirstNameImpression,
  useTrackLeadCaptureFirstNameSubmit,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { Close } from '@oneToOne/shared/assets/Close'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding'

import { ImpressionContainer } from '@core/components/ImpressionContainer'
import { Markdown } from '@core/components/Markdown'
import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

interface Props {
  onSubmit: (firstName: string) => void
}

/**
 * RequestVisitorFirstName component
 */
export const RequestVisitorFirstName: FC<Props> = ({ onSubmit }) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()
  const firstNameInputId = useId()

  const params = useStore(paramsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()
  const trackLeadCaptureFirstNameImpression =
    useTrackLeadCaptureFirstNameImpression()
  const trackLeadCaptureFirstNameSubmit = useTrackLeadCaptureFirstNameSubmit()

  const [firstName, setFirstName] = useState('')

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      if (!firstName) {
        return
      }
      trackLeadCaptureFirstNameSubmit({ first_name: firstName })
      onSubmit(firstName)
    },
    [firstName, onSubmit, trackLeadCaptureFirstNameSubmit],
  )

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <div className={tw?.('absolute')}>
        <ImpressionContainer trackingFn={trackLeadCaptureFirstNameImpression} />
      </div>
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Conversation`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label="close" />}
      />
      <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
        <div
          className={tw?.(
            menuCoverImage(
              chatChannelConfig?.widget_style?.menu_cover_image_url,
            ),
          )}
        ></div>
      </div>
      <div className={tw?.('px-10', 'pt-9', 'pb-7')}>
        <div className={tw?.(contentSection, 'flex', 'flex-col', 'gap-2.5')}>
          <p
            className={tw?.(
              requestVisitorInformationDescription,
              'text-sm',
              'font-medium',
              'mb-4',
            )}
          >
            {t('oneToOne:To better service you, can I get your name?')}
          </p>
          <form className={tw?.('w-full')} onSubmit={handleSubmit}>
            <label
              className={tw?.(
                requestVisitorInformationFormLabel,
                'text-sm',
                'font-medium',
                'mb-4',
              )}
              htmlFor={firstNameInputId}
            >
              {t(`oneToOne:First Name`)}
            </label>
            <input
              ref={(element) => {
                element?.focus()
              }}
              id={firstNameInputId}
              type="text"
              name="fwVisitorFirstName"
              placeholder={t(`oneToOne:First Name`)}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={tw?.(
                requestVisitorInformationInput,
                'w-full',
                'py-2',
                'px-3',
                'mb-2',
                'border',
                'border-gray-300',
                'focus:border-blue-500',
                'focus:outline-none',
                'rounded-sm',
              )}
              data-testid={FIRST_NAME_INPUT}
            />
            <p
              className={tw?.('text-xs', 'font-medium', 'mb-2', 'text-red-500')}
            >
              &nbsp;
            </p>
            {chatChannelConfig?.config?.legal_statement ? (
              <div
                className={tw?.(requestVisitorInformationPrivacyInfo, 'mb-8')}
              >
                <Markdown>{chatChannelConfig.config.legal_statement}</Markdown>
              </div>
            ) : null}
            <div className={tw?.('w-full', 'flex')}>
              <Button
                className={tw?.(okButton)}
                disabled={!firstName}
                type="submit"
                data-testid={NEXT_BUTTON}
                block
              >
                {t(`oneToOne:Next`)}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding
            dark={true}
            utmContent={utmContent}
            lang={params.lang}
          />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
